import React, { useCallback, useMemo, useState, useRef } from 'react';
import clsx from 'clsx';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import {
  VehiclePaintMeasurementProps,
  MeasurementArea,
  AreaPosition,
} from './VehiclePaintMeasurement.types';
import { VehicleView } from './VehicleView';
import { getVehicleAreasMap } from './areasMapping';
import { PaintThicknessLegend } from '../PaintThicknessLegend';
import {
  getPaintStatusFromValue,
  paintStatusConfig,
  formatMeasurementRange,
} from './paintStatusConfig';
import { SliderNavigation } from './SliderNavigation';
import {
  MeasurementsMobileGrid,
  MeasurementsMobileGridItem,
} from '../shared/MeasurementsMobileGrid';
import { FixedElementBadge } from '../shared/FixedElementBadge';
import { useMobileStickyNavigation } from '@/hooks/useMobileStickyNavigation';

// Helper: oblicza parametry grida z sub-kolumnami (LCM) dla równego wypełnienia ostatniego wiersza
function getGridParams(itemCount: number, maxCols: number, cellWidth: number) {
  const cols = Math.min(itemCount, maxCols);
  const itemsInLastRow = itemCount % cols || cols;
  const isPartial = itemsInLastRow < cols;
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  const lcm = (a: number, b: number) => (a * b) / gcd(a, b);
  const subCols = isPartial ? lcm(cols, itemsInLastRow) : cols;
  const subCellWidth = (cols * cellWidth) / subCols;
  const normalSpan = subCols / cols;
  const lastRowSpan = isPartial ? subCols / itemsInLastRow : 1;
  return {
    cols,
    subCols,
    subCellWidth,
    normalSpan,
    lastRowSpan,
    itemsInLastRow,
  };
}

/**
 * Komponent wizualizacji pomiarów grubości lakieru pojazdu
 *
 * Wyświetla 3 widoki pojazdu (lewy, górny, prawy) z kolorowymi obszarami
 * oraz legendę statusów i tabelę szczegółów.
 *
 * @example
 * ```tsx
 * <VehiclePaintMeasurement
 *   vehicleType="sedan"
 *   measurementAreas={[
 *     { nameId: '1', status: 'RANGE_50_100', value: 85, isFixed: false, isMeasured: true },
 *     { nameId: '13', status: 'RANGE_400_900', value: 450, isFixed: true, isMeasured: true },
 *   ]}
 * />
 * ```
 */
export const VehiclePaintMeasurement: React.FC<
  VehiclePaintMeasurementProps
> = ({ vehicleType, measurementAreas, className }) => {
  // State dla śledzenia aktywnego slajdu
  const [activeSlide, setActiveSlide] = useState(0);
  const swiperRef = useRef<SwiperType | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const {
    isActive: isMobileNavigationActive,
    stickyTop: mobileNavigationStickyTop,
  } = useMobileStickyNavigation(sectionRef);

  // State dla podświetlania obszarów (hover/select w gridach)
  const [hoveredArea, setHoveredArea] = useState<AreaPosition | null>(null);
  const [selectedArea, setSelectedArea] = useState<AreaPosition | null>(null);
  const highlightedArea = hoveredArea ?? selectedArea;

  const handleAreaClick = useCallback((nameId: AreaPosition) => {
    setSelectedArea((prev) => (prev === nameId ? null : nameId));
  }, []);

  const handleAreaHover = useCallback((nameId: AreaPosition | null) => {
    setHoveredArea(nameId);
  }, []);

  // Grupowanie obszarów po widokach - użyj areasMapping żeby określić co pokazać
  const groupedAreas = useMemo(() => {
    const groups: {
      left: MeasurementArea[];
      top: MeasurementArea[];
      right: MeasurementArea[];
    } = {
      left: [],
      top: [],
      right: [],
    };

    if (!measurementAreas) return groups;

    // Pobierz mapę obszarów dla danego typu pojazdu
    const areasMappingForType = getVehicleAreasMap(vehicleType);

    // Iteruj po wszystkich widokach i ich kluczach z areasMapping
    (['left', 'top', 'right'] as const).forEach((view) => {
      const viewMapping = areasMappingForType[view];

      // Używamy Object.entries() aby mieć pewność że mamy dostęp do kluczy i wartości
      const entries = Object.entries(viewMapping);

      entries.forEach(([nameIdKey, areaDefinition]) => {
        // Pomijamy jeśli nie ma definicji obszaru
        if (!areaDefinition) {
          return;
        }

        // Normalizuj klucz do stringa dla porównania
        const normalizedKey = String(nameIdKey);

        // Znajdź dane dla tego nameId w measurementAreas
        // Porównujemy znormalizowane wartości (oba jako stringi)
        const measurement = measurementAreas.find((m) => {
          const normalizedNameId = String(m.nameId);
          return normalizedNameId === normalizedKey;
        });

        if (measurement) {
          groups[view].push(measurement);
        }
      });
    });

    return groups;
  }, [measurementAreas, vehicleType]);

  return (
    <div
      ref={sectionRef}
      className={clsx('relative w-full', className)}
      data-mobile-sticky-navigation-section="true"
    >
      <div
        aria-label="Nawigacja widoków pojazdu"
        aria-hidden={!isMobileNavigationActive}
        data-testid="paint-mobile-slider-navigation"
        role="navigation"
        style={{ top: mobileNavigationStickyTop }}
        className={clsx(
          'sticky z-30 h-[156px] transition-[top] duration-150 ease-out motion-reduce:transition-none bg-gradient-to-t from-[#0B2460] via-[#0B2460]/80 to-transparent lg:hidden',
          !isMobileNavigationActive && 'pointer-events-none invisible',
        )}
      >
        <SliderNavigation
          activeIndex={activeSlide}
          onNavigate={(index) => {
            swiperRef.current?.slideTo(index);
          }}
        />
      </div>
      {/* Nagłówek sekcji */}
      {/* <SectionSubtitle
        title="Pomiary lakieru"
        description="Kontrola grubości lakieru w kluczowych punktach pojazdu."
      /> */}

      {/* Widoki pojazdu */}
      <div
        className="relative -mt-[156px] overflow-hidden p-8 lg:mt-0 lg:pt-9 print:overflow-visible"
        style={{
          background: 'linear-gradient(to top, #0B2460 0%, #111438 100%)',
        }}
      >
        <div className="mb-8 text-[18px] font-bold leading-[14px] text-white lg:text-[26px]">
          Pomiary lakieru
        </div>
        {/* Tło SVG Grid - inline SVG */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M 40 0 V 40 H 0' fill='none' stroke='rgba(255,255,255,0.03)'/%3E%3C/svg%3E")`,
            backgroundSize: '40px 40px',
          }}
        />

        {/* Widoki pojazdu */}
        <div className="-mx-8 flex max-w-[1268px] flex-col-reverse lg:mx-0 lg:flex-row">
          {/* Desktop: Grid layout */}
          <div className="relative z-10 hidden flex-1 grid-cols-3 lg:grid">
            <div className="flex flex-col items-start">
              <VehicleView
                view="left"
                vehicleType={vehicleType}
                areas={groupedAreas.left}
                label="Strona lewa"
                isMobile={false}
                highlightedArea={highlightedArea}
                onAreaHover={handleAreaHover}
                onAreaClick={handleAreaClick}
              />
              <div className="lg:ml-[7%]">
                <div className="mb-2 mt-[100px] text-[18px] font-semibold leading-[14px] text-white">
                  Pomiary lakieru
                </div>
                <div className="font-regular mb-8 text-[14px] leading-[14px] text-blue-300">
                  Wartość zmierzona w [μm]
                </div>
                {(() => {
                  const g = getGridParams(groupedAreas.left.length, 3, 80);
                  return (
                    <div
                      className="grid w-fit overflow-visible rounded-[15px] bg-navy-900"
                      style={{
                        gridTemplateColumns: `repeat(${g.subCols}, ${g.subCellWidth}px)`,
                      }}
                    >
                      {groupedAreas.left.map((area, index) => {
                        const totalRows = Math.ceil(
                          groupedAreas.left.length / g.cols,
                        );
                        const currentRow = Math.floor(index / g.cols);
                        const isFirstRow = currentRow === 0;
                        const isLastRow = currentRow === totalRows - 1;
                        const isPartialLastRow =
                          isLastRow && g.itemsInLastRow < g.cols;
                        const span = isPartialLastRow
                          ? g.lastRowSpan
                          : g.normalSpan;
                        const posInRow = index % g.cols;
                        const isFirstInRow = posInRow === 0;
                        const isLastInRow =
                          posInRow === g.cols - 1 ||
                          index === groupedAreas.left.length - 1;
                        const isSelected = selectedArea === area.nameId;
                        return (
                          <div
                            key={area.nameId}
                            className={clsx(
                              'relative flex cursor-pointer flex-col items-center gap-2.5 px-1 py-4 text-center transition-colors',
                              !isLastInRow && 'border-r',
                              !isLastRow && 'border-b',
                              isSelected && 'bg-blue-900/30',
                              isFirstRow && isFirstInRow && 'rounded-tl-[15px]',
                              isFirstRow && isLastInRow && 'rounded-tr-[15px]',
                              isLastRow && isFirstInRow && 'rounded-bl-[15px]',
                              isLastRow && isLastInRow && 'rounded-br-[15px]',
                            )}
                            style={{
                              borderColor: '#353a6b',
                              gridColumn: `span ${span}`,
                            }}
                            onClick={() => handleAreaClick(area.nameId)}
                            onMouseEnter={() => handleAreaHover(area.nameId)}
                            onMouseLeave={() => handleAreaHover(null)}
                          >
                            {area.isFixed && (
                              <div className="absolute left-1/2 top-[-13px] z-10 -translate-x-1/2 lg:top-[-16px]">
                                <FixedElementBadge disclaimerText="Element naprawiany" />
                              </div>
                            )}
                            <div className="text-base font-medium leading-[14px] text-blue-300">
                              {area.nameId}
                            </div>
                            <div className="text-[13px] font-semibold leading-[14px] text-white">
                              {area.minMeasurement != null &&
                              area.maxMeasurement != null
                                ? `${formatMeasurementRange(area.minMeasurement, area.maxMeasurement)}`
                                : paintStatusConfig[
                                    getPaintStatusFromValue(
                                      area.value,
                                      area.notApplicable,
                                    )
                                  ].range}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="flex flex-col items-center">
              <VehicleView
                view="top"
                vehicleType={vehicleType}
                areas={groupedAreas.top}
                label=""
                isMobile={false}
                highlightedArea={highlightedArea}
                onAreaHover={handleAreaHover}
                onAreaClick={handleAreaClick}
              />
              <div>
                <div
                  className="invisible mb-2 mt-[100px] text-[18px] font-semibold leading-[14px]"
                  aria-hidden="true"
                >
                  &nbsp;
                </div>
                <div
                  className="invisible mb-8 text-[14px] leading-[14px]"
                  aria-hidden="true"
                >
                  &nbsp;
                </div>
                {(() => {
                  const g = getGridParams(groupedAreas.top.length, 3, 80);
                  return (
                    <div
                      className="grid w-fit overflow-visible rounded-[15px] bg-navy-900"
                      style={{
                        gridTemplateColumns: `repeat(${g.subCols}, ${g.subCellWidth}px)`,
                      }}
                    >
                      {groupedAreas.top.map((area, index) => {
                        const totalRows = Math.ceil(
                          groupedAreas.top.length / g.cols,
                        );
                        const currentRow = Math.floor(index / g.cols);
                        const isFirstRow = currentRow === 0;
                        const isLastRow = currentRow === totalRows - 1;
                        const isPartialLastRow =
                          isLastRow && g.itemsInLastRow < g.cols;
                        const span = isPartialLastRow
                          ? g.lastRowSpan
                          : g.normalSpan;
                        const posInRow = index % g.cols;
                        const isFirstInRow = posInRow === 0;
                        const isLastInRow =
                          posInRow === g.cols - 1 ||
                          index === groupedAreas.top.length - 1;
                        const isSelected = selectedArea === area.nameId;
                        return (
                          <div
                            key={area.nameId}
                            className={clsx(
                              'relative flex cursor-pointer flex-col items-center gap-2.5 px-1 py-4 text-center transition-colors',
                              !isLastInRow && 'border-r',
                              !isLastRow && 'border-b',
                              isSelected && 'bg-blue-900/30',
                              isFirstRow && isFirstInRow && 'rounded-tl-[15px]',
                              isFirstRow && isLastInRow && 'rounded-tr-[15px]',
                              isLastRow && isFirstInRow && 'rounded-bl-[15px]',
                              isLastRow && isLastInRow && 'rounded-br-[15px]',
                            )}
                            style={{
                              borderColor: '#353a6b',
                              gridColumn: `span ${span}`,
                            }}
                            onClick={() => handleAreaClick(area.nameId)}
                            onMouseEnter={() => handleAreaHover(area.nameId)}
                            onMouseLeave={() => handleAreaHover(null)}
                          >
                            {area.isFixed && (
                              <div className="absolute left-1/2 top-[-13px] z-10 -translate-x-1/2 lg:top-[-16px]">
                                <FixedElementBadge disclaimerText="Element naprawiany" />
                              </div>
                            )}
                            <div className="text-base font-medium leading-[14px] text-blue-300">
                              {area.nameId}
                            </div>
                            <div className="text-[13px] font-semibold leading-[14px] text-white">
                              {area.minMeasurement != null &&
                              area.maxMeasurement != null
                                ? `${formatMeasurementRange(area.minMeasurement, area.maxMeasurement)}`
                                : paintStatusConfig[
                                    getPaintStatusFromValue(
                                      area.value,
                                      area.notApplicable,
                                    )
                                  ].range}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="relative flex flex-col items-center">
              <VehicleView
                view="right"
                vehicleType={vehicleType}
                areas={groupedAreas.right}
                label="Strona prawa"
                isMobile={false}
                highlightedArea={highlightedArea}
                onAreaHover={handleAreaHover}
                onAreaClick={handleAreaClick}
              />
              <div className="ml-auto">
                <div
                  className="invisible mb-2 mt-[100px] text-[18px] font-semibold leading-[14px]"
                  aria-hidden="true"
                >
                  &nbsp;
                </div>
                <div className="relative left-[-5px] top-[-190px] hidden min-[1600px]:block">
                  <PaintThicknessLegend />
                </div>
                <div
                  className="absolute right-0 hidden lg:block min-[1600px]:hidden"
                  style={{
                    bottom:
                      vehicleType === 'van'
                        ? '160px'
                        : vehicleType === 'hatchback'
                          ? '100px'
                          : '160px',
                  }}
                >
                  <PaintThicknessLegend variant="mobile" />
                </div>
                <div
                  className="invisible mb-8 text-[14px] leading-[14px]"
                  aria-hidden="true"
                >
                  &nbsp;
                </div>
                {(() => {
                  const g = getGridParams(groupedAreas.right.length, 3, 80);
                  return (
                    <div
                      className="grid w-fit overflow-visible rounded-[15px] bg-navy-900"
                      style={{
                        gridTemplateColumns: `repeat(${g.subCols}, ${g.subCellWidth}px)`,
                      }}
                    >
                      {groupedAreas.right.map((area, index) => {
                        const totalRows = Math.ceil(
                          groupedAreas.right.length / g.cols,
                        );
                        const currentRow = Math.floor(index / g.cols);
                        const isFirstRow = currentRow === 0;
                        const isLastRow = currentRow === totalRows - 1;
                        const isPartialLastRow =
                          isLastRow && g.itemsInLastRow < g.cols;
                        const span = isPartialLastRow
                          ? g.lastRowSpan
                          : g.normalSpan;
                        const posInRow = index % g.cols;
                        const isFirstInRow = posInRow === 0;
                        const isLastInRow =
                          posInRow === g.cols - 1 ||
                          index === groupedAreas.right.length - 1;
                        const isSelected = selectedArea === area.nameId;
                        return (
                          <div
                            key={area.nameId}
                            className={clsx(
                              'relative flex cursor-pointer flex-col items-center gap-2.5 px-1 py-4 text-center transition-colors',
                              !isLastInRow && 'border-r',
                              !isLastRow && 'border-b',
                              isSelected && 'bg-blue-900/30',
                              isFirstRow && isFirstInRow && 'rounded-tl-[15px]',
                              isFirstRow && isLastInRow && 'rounded-tr-[15px]',
                              isLastRow && isFirstInRow && 'rounded-bl-[15px]',
                              isLastRow && isLastInRow && 'rounded-br-[15px]',
                            )}
                            style={{
                              borderColor: '#353a6b',
                              gridColumn: `span ${span}`,
                            }}
                            onClick={() => handleAreaClick(area.nameId)}
                            onMouseEnter={() => handleAreaHover(area.nameId)}
                            onMouseLeave={() => handleAreaHover(null)}
                          >
                            {area.isFixed && (
                              <div className="absolute left-1/2 top-[-13px] z-10 -translate-x-1/2 lg:top-[-16px]">
                                <FixedElementBadge disclaimerText="Element naprawiany" />
                              </div>
                            )}
                            <div className="text-base font-medium leading-[14px] text-blue-300">
                              {area.nameId}
                            </div>
                            <div className="text-[13px] font-semibold leading-[14px] text-white">
                              {area.minMeasurement != null &&
                              area.maxMeasurement != null
                                ? `${formatMeasurementRange(area.minMeasurement, area.maxMeasurement)}`
                                : paintStatusConfig[
                                    getPaintStatusFromValue(
                                      area.value,
                                      area.notApplicable,
                                    )
                                  ].range}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Mobile: Swiper slider */}
          <div className="relative z-10 -mb-8 w-full flex-1 pb-8 lg:mb-0 lg:hidden lg:pb-8">
            <div className="relative z-20 block w-full px-4 lg:hidden">
              <PaintThicknessLegend variant="mobile" />
            </div>

            {/* Left edge gradient */}
            <div className="pointer-events-none absolute bottom-0 left-0 top-0 z-10 w-[89px] bg-gradient-to-r from-[#11153b]/90 to-[#11153b]/0" />
            {/* Right edge gradient */}
            <div className="pointer-events-none absolute bottom-0 right-0 top-0 z-10 w-[89px] bg-gradient-to-l from-[#11153b]/90 to-[#11153b]/0" />
            <Swiper
              slidesPerView={1.2}
              spaceBetween={0}
              centeredSlides={true}
              className="w-full !overflow-x-clip !overflow-y-visible"
              autoHeight
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
              }}
              onSlideChange={(swiper) => {
                setActiveSlide(swiper.activeIndex);
              }}
            >
              <SwiperSlide>
                <VehicleView
                  view="left"
                  vehicleType={vehicleType}
                  areas={groupedAreas.left}
                  label="Strona lewa"
                  isMobile={true}
                  highlightedArea={highlightedArea}
                  onAreaHover={handleAreaHover}
                  onAreaClick={handleAreaClick}
                />
                <div className="mb-4 mt-8 text-center text-[18px] font-semibold leading-[14px] text-white">
                  Pomiary lakieru
                </div>
                <div className="font-regular mb-8 text-center text-[14px] leading-[14px] text-blue-300">
                  Wartość zmierzona w [μm]
                </div>
                <MeasurementsMobileGrid
                  items={groupedAreas.left.map(
                    (area): MeasurementsMobileGridItem => ({
                      id: area.nameId,
                      label: area.nameId,
                      value:
                        area.minMeasurement != null &&
                        area.maxMeasurement != null
                          ? `${formatMeasurementRange(area.minMeasurement, area.maxMeasurement)}`
                          : paintStatusConfig[
                              getPaintStatusFromValue(
                                area.value,
                                area.notApplicable,
                              )
                            ].range,
                      isFixed: area.isFixed,
                    }),
                  )}
                  selectedItemId={selectedArea}
                  onItemClick={handleAreaClick}
                  onItemHover={handleAreaHover}
                />
              </SwiperSlide>

              <SwiperSlide>
                <VehicleView
                  view="top"
                  vehicleType={vehicleType}
                  areas={groupedAreas.top}
                  label=""
                  isMobile={true}
                  highlightedArea={highlightedArea}
                  onAreaHover={handleAreaHover}
                  onAreaClick={handleAreaClick}
                  // className="h-[640px] xs:h-auto"
                />
                <div className="mb-4 mt-8 text-center text-[18px] font-semibold leading-[14px] text-white">
                  Pomiary lakieru
                </div>
                <div className="font-regular mb-8 text-center text-[14px] leading-[14px] text-blue-300">
                  Wartość zmierzona w [μm]
                </div>
                <MeasurementsMobileGrid
                  items={groupedAreas.top.map(
                    (area): MeasurementsMobileGridItem => ({
                      id: area.nameId,
                      label: area.nameId,
                      value:
                        area.minMeasurement != null &&
                        area.maxMeasurement != null
                          ? `${formatMeasurementRange(area.minMeasurement, area.maxMeasurement)}`
                          : paintStatusConfig[
                              getPaintStatusFromValue(
                                area.value,
                                area.notApplicable,
                              )
                            ].range,
                      isFixed: area.isFixed,
                    }),
                  )}
                  selectedItemId={selectedArea}
                  onItemClick={handleAreaClick}
                  onItemHover={handleAreaHover}
                />
              </SwiperSlide>

              <SwiperSlide>
                <VehicleView
                  view="right"
                  vehicleType={vehicleType}
                  areas={groupedAreas.right}
                  label="Strona prawa"
                  isMobile={true}
                  highlightedArea={highlightedArea}
                  onAreaHover={handleAreaHover}
                  onAreaClick={handleAreaClick}
                />
                <div className="mb-4 mt-8 text-center text-[18px] font-semibold leading-[14px] text-white">
                  Pomiary lakieru
                </div>
                <div className="font-regular mb-8 text-center text-[14px] leading-[14px] text-blue-300">
                  Wartość zmierzona w [μm]
                </div>
                <MeasurementsMobileGrid
                  items={groupedAreas.right.map(
                    (area): MeasurementsMobileGridItem => ({
                      id: area.nameId,
                      label: area.nameId,
                      value:
                        area.minMeasurement != null &&
                        area.maxMeasurement != null
                          ? `${formatMeasurementRange(area.minMeasurement, area.maxMeasurement)}`
                          : paintStatusConfig[
                              getPaintStatusFromValue(
                                area.value,
                                area.notApplicable,
                              )
                            ].range,
                      isFixed: area.isFixed,
                    }),
                  )}
                  selectedItemId={selectedArea}
                  onItemClick={handleAreaClick}
                  onItemHover={handleAreaHover}
                />
              </SwiperSlide>
            </Swiper>

            <div
              aria-hidden="true"
              aria-label="Nawigacja widoków pojazdu"
              data-testid="paint-inline-slider-navigation"
              role="navigation"
              className="pointer-events-none invisible lg:hidden"
            >
              <SliderNavigation
                activeIndex={activeSlide}
                onNavigate={(index) => {
                  swiperRef.current?.slideTo(index);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Legenda statusów */}
    </div>
  );
};
